import { Schemas } from "@kollectivia/shared";
import { useTranslation } from "react-i18next";

import { useFile } from "../lib/queries/files";

type Attachment = { file_id: string; file_name: string; sha256: string | null; size_bytes: number };
/** Presentation of an exact readiness attachment, without renaming the stored artifact. */
export function PreparedAttachment({
  file,
  onDownload,
}: {
  file: Attachment;
  onDownload?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const metadata = useFile(file.file_id);
  const verified =
    typeof file.sha256 === "string" &&
    !metadata.isError &&
    metadata.data?.id === file.file_id &&
    metadata.data.sha256 === file.sha256 &&
    metadata.data.size_bytes === file.size_bytes
      ? metadata.data
      : undefined;
  const label = verified
    ? t(`files.kinds.${verified.kind}`, {
        defaultValue: t(`files.generated_kinds.${verified.kind}`, {
          defaultValue: t("files.generic_kind"),
        }),
      })
    : file.file_name;
  const type =
    verified?.mime_type === "application/pdf"
      ? "PDF"
      : verified?.mime_type === Schemas.XLSX_MIME
        ? "XLSX"
        : null;
  return (
    <div className="min-w-0 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        {onDownload ? (
          <button
            type="button"
            className="text-brand-700 min-h-11 break-words rounded px-2 text-left font-medium underline focus-visible:ring-2"
            onClick={() => onDownload(file.file_id)}
          >
            {label}
          </button>
        ) : (
          <span className="break-words font-medium">{label}</span>
        )}
        {type && (
          <span className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-700">{type}</span>
        )}
      </div>
      {verified && (
        <details className="text-xs text-slate-600">
          <summary className="min-h-11 cursor-pointer py-3 focus-visible:ring-2">
            {t("flow.file_details")}
          </summary>
          <p className="break-words [overflow-wrap:anywhere]">{file.file_name}</p>
        </details>
      )}
    </div>
  );
}
